import axios, { AxiosResponse, AxiosError } from "axios";

import {
  AUTH_TOKEN_HEADER,
  SAMSKIP_SUBSYS,
  UserRole,
  UserType,
} from "@assets/constants";
import { Company, UserProfileViewModel, UserWebSettings } from "@assets/types";
import store from "@state/store";

const AuthAPI = process.env.AuthAPI;

class UserService {
  getUserId = (): number => {
    const { userProfile } = store.getState().userProfileSlice;
    const User = userProfile && userProfile.User ? userProfile.User : null;
    return User ? User.ID : 0;
  };

  isLoggedIn = (): boolean => {
    const userId = this.getUserId();
    return userId > 0;
  };

  isUserAdmin = (useProfileToCheck?: UserProfileViewModel): boolean => {
    const userProfile =
      useProfileToCheck ?? store.getState().userProfileSlice.userProfile;
    return (
      (userProfile &&
        userProfile.Roles?.some(
          (userRole) => userRole.RoleID === UserRole.ADMIN
        )) ||
      false
    );
  };

  isUserEmployee = (useProfileToCheck?: UserProfileViewModel): boolean => {
    const userProfile =
      useProfileToCheck ?? store.getState().userProfileSlice.userProfile;
    return (
      (userProfile && userProfile.Access.UserType === UserType.Employee) ||
      false
    );
  };

  isUserAgent = (useProfileToCheck?: UserProfileViewModel): boolean => {
    const userProfile =
      useProfileToCheck ?? store.getState().userProfileSlice.userProfile;
    return (
      (userProfile && userProfile.Access.UserType === UserType.Agent) || false
    );
  };

  isUserCustomer = (useProfileToCheck?: UserProfileViewModel): boolean => {
    const userProfile =
      useProfileToCheck ?? store.getState().userProfileSlice.userProfile;
    return (
      (userProfile && userProfile.Access.UserType === UserType.Customer) ||
      false
    );
  };

  hasUserAccessToRoles = (roles: number[]): boolean => {
    const { userProfile } = store.getState().userProfileSlice;
    return (
      (userProfile &&
        userProfile.Roles?.some((userRole) =>
          roles.some((neededRole) => neededRole === userRole.RoleID)
        )) ||
      false
    );
  };

  getUserSelectedLanguage = async () =>
    axios
      .get(`${AuthAPI}/users/selectedLanguage`, { withCredentials: true })
      .then((res: AxiosResponse<string>) => {
        return res.data.replace(/"/g, "");
      })
      .catch(() => {
        if (typeof window !== "undefined") {
          return window.localStorage.getItem("selectedLanguage") ?? "is";
        }
        return "is";
      });

  updateSelectedLanguageWebSetting = async (
    selectedLanguage: string
  ): Promise<UserWebSettings | void> => {
    const userId = this.getUserId();
    if (userId)
      return await axios
        .put(
          `${AuthAPI}/users/${userId}/access/1000/settings/SelectedLanguage`,
          `"${selectedLanguage}"`,
          {
            withCredentials: true,
            headers: { "Content-Type": "application/json" },
          }
        )
        .then((res) => res.data);
  };

  getWebSettings(user: UserProfileViewModel) {
    if (user != undefined) {
      return user.WebSettings;
    }

    return null;
  }

  getUserProfileFromAPI = async () => {
    return axios
      .get(`${AuthAPI}/auth/me`, { withCredentials: true })
      .then(async (res: AxiosResponse<UserProfileViewModel>) => {
        window.localStorage.setItem(
          AUTH_TOKEN_HEADER,
          res.headers["authorization"]
        );
        return res.data;
      })
      .catch((error: AxiosError) => {
        throw error;
      });
  };

  setUserWebSettings = async (
    field: string,
    value: string,
    user: UserProfileViewModel
  ): Promise<UserWebSettings | void> => {
    return axios
      .put(
        `${AuthAPI}/users/${user.User.ID}/access/${user.Access.SubSys}/settings/${field}`,
        `"${value}"`,
        {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        }
      )
      .then((res) => res.data);
  };

  changeUserCompany = async (partnerCode: string): Promise<Company> => {
    const userID = this.getUserId();
    return axios
      .put(
        `${AuthAPI}/users/${userID}/companies/${SAMSKIP_SUBSYS}`,
        partnerCode,
        {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        }
      )
      .then((res: AxiosResponse<Company>) => {
        return res.data;
      });
  };

  searchUserCompanies = async (searchString: string): Promise<Company[]> => {
    const userID = this.getUserId();
    if (!userID) {
      return Promise.reject("UserProfile not loaded");
    }
    if (!searchString) {
      return Promise.reject("Empty search string");
    }

    return axios
      .get(
        `${AuthAPI}/users/${userID}/companies/${SAMSKIP_SUBSYS}/search/${searchString}`,
        {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        }
      )
      .then((res: AxiosResponse<Company[]>) => {
        return res.data;
      });
  };
}

export default new UserService();
